import React, { FC, useEffect, useState } from 'react'
import { Box, Menu, MenuItem, Paper } from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { DataGridPremium, GridActionsCellItem, GridColDef, GridPaginationModel } from '@mui/x-data-grid-premium'
import format from 'date-fns/format'
import { SensorConfigurationAnalyticsRow, SensorConfigurationAnalyticsRowMeasurements } from 'src/api'
import { SensorConfigurationRequestStatusChip } from 'src/components/columns/sensorConfigurationRequest'
import { getConfigurationRequestStatusMeta } from 'src/components/columns/sensorConfigurationRequestHelpers'
import { DataLoad, InnerLink, TableToolbar } from 'src/components/ui'
import { SearchForm } from 'src/components/forms/common/search'
import { ConfigurationAnalyticsFilters } from 'src/components/forms/sensor/configurationAnalyticsFilters'
import { useSensorConfigurationAnalyticsListRequest } from 'src/hooks/api'
import { useSensorConfiguring } from 'src/hooks/api/useSensorConfiguring'
import { useAddPopupMessage, useQueryParams, useQueryParamsState } from 'src/hooks/ui'
import { SearchParamsKeys } from 'src/enums'
import { defaultRowsValuePerPage } from 'src/config'
import { formatNullableNumber } from 'src/utils/formatters'
import { configTypeLabels } from './configTypes'

const defaultPerPage = 25
const defaultPage = 1
const initialQueryParams = {
  [SearchParamsKeys.Page]: `${defaultPage}`,
  [SearchParamsKeys.RowsPerPage]: `${defaultPerPage}`
}

// The grid gets a definite height (the collapsible section around it has none), sized to the rows
// on the current page and capped so a large page scrolls inside the grid instead of stretching the
// page. Chrome height = toolbar (35) + column headers (56) + footer (53) + horizontal scrollbar.
const maxTableHeight = 800
const tableChromeHeight = 160
const rowHeight = 52
const getTableHeight = (rowsOnPage: number) => Math.min(maxTableHeight, tableChromeHeight + Math.max(rowsOnPage, 1) * rowHeight)

const formatPeriod = ({ value }: { value?: number | null }) => formatNullableNumber(value)

// All measurement types the backend reports periods for, in display order. Each gets a
// monitoring + reporting column; both are null when that measurement (e.g. BLE) is disabled.
const MEASUREMENTS: { key: keyof SensorConfigurationAnalyticsRowMeasurements; label: string }[] = [
  { key: 'temperature', label: 'Temperature' },
  { key: 'humidity', label: 'Humidity' },
  { key: 'pressure', label: 'Pressure' },
  { key: 'light', label: 'Light' },
  { key: 'location', label: 'Location' },
  { key: 'accelerometer', label: 'Accelerometer' },
  { key: 'gyro', label: 'Gyro' },
  { key: 'battery', label: 'Battery' },
  { key: 'rfSignal', label: 'RF Signal' },
  { key: 'ble', label: 'BLE' }
]

const measurementColumns: GridColDef<SensorConfigurationAnalyticsRow>[] = MEASUREMENTS.flatMap(({ key, label }) => [
  {
    field: `${key}MonitorPeriod`,
    headerName: `${label} monitoring`,
    flex: 1,
    minWidth: 150,
    sortable: false,
    disableColumnMenu: true,
    valueGetter: ({ row }) => row.measurements?.[key]?.monitorPeriod ?? null,
    valueFormatter: formatPeriod
  },
  {
    field: `${key}ReportPeriod`,
    headerName: `${label} reporting`,
    flex: 1,
    minWidth: 150,
    sortable: false,
    disableColumnMenu: true,
    valueGetter: ({ row }) => row.measurements?.[key]?.reportPeriod ?? null,
    valueFormatter: formatPeriod
  }
])

const baseColumns: GridColDef<SensorConfigurationAnalyticsRow>[] = [
  {
    field: 'uniqueId',
    headerName: 'ID',
    flex: 1,
    minWidth: 160,
    sortable: false,
    disableColumnMenu: true,
    renderCell: ({ value }: { value?: string }) => (value ? <InnerLink to={`/sensors/${value}`}>{value}</InnerLink> : '-')
  },
  { field: 'imei', headerName: 'IMEI', flex: 1.5, minWidth: 160, sortable: false, disableColumnMenu: true },
  {
    field: 'configType',
    headerName: 'Configuration type',
    flex: 1.5,
    minWidth: 180,
    sortable: false,
    disableColumnMenu: true,
    valueFormatter: ({ value }: { value?: string }) => (value ? configTypeLabels[value] || value : '-')
  },
  {
    field: 'configRequestAt',
    headerName: 'Config request datetime',
    flex: 1.5,
    minWidth: 200,
    sortable: false,
    disableColumnMenu: true,
    valueFormatter: ({ value }: { value?: string | null }) => (value ? format(new Date(value), 'PPpp') : '-')
  },
  {
    field: 'configRequestStatus',
    headerName: 'Config request status',
    flex: 1.5,
    minWidth: 180,
    sortable: false,
    disableColumnMenu: true,
    valueGetter: ({ row }) => (row.configRequestStatus ? getConfigurationRequestStatusMeta(row.configRequestStatus).label : ''),
    renderCell: ({ row }) => <SensorConfigurationRequestStatusChip status={row.configRequestStatus} />
  },
  ...measurementColumns
]

export const SensorConfigurationSensorsTable: FC = () => {
  const queryParamsState = useQueryParamsState()
  const [queryParams, setQueryParams] = useQueryParams(initialQueryParams)
  const addPopupMessage = useAddPopupMessage()

  const { data, error, isInitialLoading, isError } = useSensorConfigurationAnalyticsListRequest({
    params: { ...queryParamsState }
  })

  const [rowCountState, setRowCountState] = useState(data?.count || 0)
  useEffect(() => {
    setRowCountState((prev) => (data?.count !== undefined ? data.count : prev))
  }, [data?.count])

  const [currentRow, setCurrentRow] = useState<SensorConfigurationAnalyticsRow | null>(null)
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, row: SensorConfigurationAnalyticsRow) => {
    setCurrentRow(row)
    setMenuAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setCurrentRow(null)
    setMenuAnchorEl(null)
  }

  // Same endpoint the "Configure sensor" button uses on the sensor detail page.
  const configuringRequest = useSensorConfiguring()

  const handleSendDefaultConfiguration = () => {
    const uniqueId = currentRow?.uniqueId
    if (uniqueId) {
      configuringRequest.mutate(uniqueId, {
        onSuccess: () => addPopupMessage({ text: 'Default configuration sent to the sensor', type: 'success' }),
        onError: ({ message: text }) => addPopupMessage({ text, type: 'error' })
      })
    }
    handleCloseMenu()
  }

  const columns: GridColDef<SensorConfigurationAnalyticsRow>[] = [
    ...baseColumns,
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 30,
      getActions: ({ row }) => [
        <GridActionsCellItem key="menu" icon={<MoreVertIcon />} label="Actions" onClick={(event: React.MouseEvent<HTMLElement>) => handleOpenMenu(event, row)} color="inherit" />
      ]
    }
  ]

  const page = queryParams[SearchParamsKeys.Page] ? parseInt(queryParams[SearchParamsKeys.Page], 10) - 1 : defaultPage - 1
  const pageSize = parseInt(queryParams[SearchParamsKeys.RowsPerPage], 10) || defaultPerPage

  const handleChangePaginationModel = (pagination: GridPaginationModel) => {
    setQueryParams(
      {
        [SearchParamsKeys.Page]: `${pagination.page + 1}`,
        [SearchParamsKeys.RowsPerPage]: `${pagination.pageSize}`
      },
      [SearchParamsKeys.Page, SearchParamsKeys.RowsPerPage]
    )
  }

  return (
    <>
      <Box mb={2}>
        <SearchForm placeholder="Enter at least three symbols to start searching" />
      </Box>
      <Box mb={2}>
        <ConfigurationAnalyticsFilters />
      </Box>
      <DataLoad isLoading={false} isError={isError} errorMessage={error?.message}>
        <Paper sx={{ height: getTableHeight(data?.rows?.length || 0), maxHeight: '80vh' }}>
          <DataGridPremium<SensorConfigurationAnalyticsRow>
            pagination
            paginationMode="server"
            disableRowSelectionOnClick
            rows={data?.rows || []}
            columns={columns}
            pinnedColumns={{ right: ['actions'] }}
            rowCount={rowCountState}
            loading={isInitialLoading}
            getRowId={(row) => row.id ?? row.imei ?? ''}
            paginationModel={{ page, pageSize }}
            pageSizeOptions={defaultRowsValuePerPage}
            onPaginationModelChange={handleChangePaginationModel}
            rowThreshold={2}
            columnThreshold={2}
            localeText={{ columnMenuSortAsc: 'Sort A-Z', columnMenuSortDesc: 'Sort Z-A' }}
            slots={{ toolbar: () => <TableToolbar exportedFileName={`${document.title}_sensors`} /> }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeaderTitleContainerContent': { paddingLeft: '5px', overflow: 'visible' },
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'normal',
                lineHeight: 1,
                fontSize: '13px',
                fontWeight: 400,
                color: 'rgba(0, 0, 0, 0.6)',
                overflow: 'visible'
              }
            }}
          />
        </Paper>
      </DataLoad>
      <Menu anchorEl={menuAnchorEl} open={!!menuAnchorEl} onClose={handleCloseMenu}>
        <MenuItem disabled={configuringRequest.isLoading || !currentRow?.uniqueId} onClick={handleSendDefaultConfiguration}>
          Send default configuration
        </MenuItem>
      </Menu>
    </>
  )
}
